// SendTestResults.js
import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import emailjs from "@emailjs/browser";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Text,
  HStack,
  Badge,
  Progress,
  useToast,
} from "@chakra-ui/react";
import { getConfigValue } from "../../config/runtimeConfig";

const SendTestResults = ({ score, total, userAnswers, quizQuestions, onRetake }) => {
  const toast = useToast();
  const [submitted, setSubmitted] = useState(false);
  const emailFrom = getConfigValue(
    "REACT_APP_EMAILJS_FROM_EMAIL",
    process.env.REACT_APP_EMAILJS_FROM_EMAIL,
    "hr@teliviit.com"
  );
  const emailTo = getConfigValue(
    "REACT_APP_EMAILJS_TO_EMAIL",
    process.env.REACT_APP_EMAILJS_TO_EMAIL,
    "hr@teliviit.com"
  );

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required("Name is required")
      .min(2, "Name must be at least 2 characters"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
  });

  const formik = useFormik({
    initialValues: { name: "", email: "" },
    validationSchema,
    onSubmit: async (values) => {
      setSubmitted(true); // always show the review after submit attempt
      try {
        const serviceId = getConfigValue("REACT_APP_EMAILJS_SERVICE_ID", process.env.REACT_APP_EMAILJS_SERVICE_ID);
        const templateId = getConfigValue("REACT_APP_EMAILJS_TEMPLATE_ID", process.env.REACT_APP_EMAILJS_TEMPLATE_ID);
        const userId = getConfigValue("REACT_APP_EMAILJS_USER_ID", process.env.REACT_APP_EMAILJS_USER_ID);

        const totalQuestions = quizQuestions.length;
        const easyCutoff = Math.ceil(totalQuestions / 3);
        const mediumCutoff = Math.ceil((2 * totalQuestions) / 3);
        const difficultyLabel = (index) =>
          index < easyCutoff ? 'Easy' : index < mediumCutoff ? 'Medium' : 'Hard';

        const answersSummary = quizQuestions
          .map((q, i) => {
            const ua = userAnswers.find((a) => a.questionIndex === i);
            const userAns = ua?.selected?.answerText ?? '—';
            const correctAns = q.answerOptions.find(opt => opt.isCorrect)?.answerText ?? '—';
            const result = ua?.correct ? 'Correct ✅' : 'Wrong ❌';
            return [
              `Q${i + 1} (${difficultyLabel(i)})`,
              `Question: ${q.questionText}`,
              `Your Answer: ${userAns}`,
              `Correct Answer: ${correctAns}`,
              `Result: ${result}`,
            ].join('\n');
          })
          .join('\n\n------------------------------\n\n');

        await emailjs.send(
          serviceId,
          templateId,
          {
            from_name: values.name,
            // Use a verified sender to satisfy EmailJS/provider restrictions
            from_email: emailFrom,
            // Preserve the user's email so HR can reply
            user_email: values.email,
            to_email: emailTo,
            subject: `Test Results: ${values.name}`,
            message: `From: ${values.name}\nScore: ${score} / ${total}\n\nAnswers Review:\n\n${answersSummary}`,
          },
          userId
        );

        toast({
          title: "Email Sent",
          description: "Score sent to HR successfully!",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (error) {
        console.error(error);
        toast({
          title: "Email Failed",
          description: "Failed to send test results to HR. You can still see your review below.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    },
  });

  // Calculate percentages and dynamic difficulty counts based on question count
  const percentage = ((score / total) * 100).toFixed(1);
  const totalQuestions2 = quizQuestions.length;
  const easyCut = Math.ceil(totalQuestions2 / 3);
  const medCut = Math.ceil((2 * totalQuestions2) / 3);
  const bucket = (idx) => (idx < easyCut ? 'easy' : idx < medCut ? 'medium' : 'hard');

  const difficultyScore = {
    easy: { total: 0, correct: 0 },
    medium: { total: 0, correct: 0 },
    hard: { total: 0, correct: 0 },
  };

  for (let i = 0; i < totalQuestions2; i++) {
    const b = bucket(i);
    difficultyScore[b].total += 1;
    const ua = userAnswers.find((a) => a.questionIndex === i);
    if (ua?.correct) difficultyScore[b].correct += 1;
  }

  const getProgressColor = (percent) => {
    if (percent >= 80) return "green";
    if (percent >= 50) return "yellow";
    return "red";
  };

  return (
    <Box>
      {!submitted ? (
        <Box p={6} shadow="md" borderWidth="1px" borderRadius="lg" bg="white">
          <Heading as="h2" size="xl" mb={4}>
            Submit Your Details
          </Heading>
          <Text mb={6}>Your Score: {score} / {total}</Text>
          <form onSubmit={formik.handleSubmit}>
            <VStack spacing={4}>
              <FormControl isInvalid={formik.touched.name && !!formik.errors.name}>
                <FormLabel>Name</FormLabel>
                <Input
                  name="name"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.name}
                  placeholder="Enter your name"
                />
                {formik.touched.name && formik.errors.name && (
                  <Text color="red.500" fontSize="sm">{formik.errors.name}</Text>
                )}
              </FormControl>

              <FormControl isInvalid={formik.touched.email && !!formik.errors.email}>
                <FormLabel>Email</FormLabel>
                <Input
                  name="email"
                  type="email"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                  placeholder="Enter your email"
                />
                {formik.touched.email && formik.errors.email && (
                  <Text color="red.500" fontSize="sm">{formik.errors.email}</Text>
                )}
              </FormControl>

              <Button type="submit" colorScheme="blue" width="full">
                Submit Test Results
              </Button>
            </VStack>
          </form>
        </Box>
      ) : (
        <Box>
          {/* Total Score with percentage */}
          <Box p={6} shadow="md" borderWidth="1px" borderRadius="lg" bg="white" mb={6}>
            <Heading as="h2" size="xl" mb={4}>
              Your Score: {score} / {total}
            </Heading>
            <Text mb={2}>Percentage: {percentage}%</Text>
            <Progress
              value={percentage}
              size="lg"
              colorScheme={getProgressColor(percentage)}
              mb={4}
              borderRadius="md"
            />
            <Text mb={2}>Breakdown by Difficulty:</Text>
            <Text>Easy: {difficultyScore.easy.correct} / {difficultyScore.easy.total}</Text>
            <Text>Medium: {difficultyScore.medium.correct} / {difficultyScore.medium.total}</Text>
            <Text>Hard: {difficultyScore.hard.correct} / {difficultyScore.hard.total}</Text>
            <Button mt={4} colorScheme="blue" onClick={onRetake}>Retake Quiz</Button>
          </Box>

          {/* Collapsible Review Section */}
          <VStack spacing={4} align="stretch">
  {quizQuestions.map((q, i) => {
    const userAnswer = userAnswers.find(ua => ua.questionIndex === i);

    return (
      <Box key={i} p={4} shadow="md" borderWidth="1px" borderRadius="lg">
        <HStack justify="space-between" mb={2}>
          <Text fontWeight="bold">
            Q{i + 1}. {q.questionText}
          </Text>
          <Badge colorScheme={i < 10 ? "green" : i < 30 ? "yellow" : "red"}>
            {i < 10 ? "Easy" : i < 30 ? "Medium" : "Hard"}
          </Badge>
        </HStack>

        {q.answerOptions.map((opt, j) => {
          const isUserSelected = userAnswer?.selected.answerText === opt.answerText;
          let bgColor = "gray.50";
          let prefix = "";

          if (isUserSelected && opt.isCorrect) {
            // User selected correct answer
            bgColor = "green.100";
            prefix = "✅ ";
          } else if (isUserSelected && !opt.isCorrect) {
            // User selected wrong answer
            bgColor = "red.100";
            prefix = "❌ ";
          } else if (!isUserSelected && opt.isCorrect && userAnswer?.correct === false) {
            // Correct answer highlight when user selected wrong
            bgColor = "green.100";
          }

          return (
            <Text key={j} p={2} borderRadius="md" bg={bgColor}>
              {prefix}{opt.answerText}
            </Text>
          );
        })}

        <Text mt={2} fontStyle="italic" color="green.700">
          💡 {q.explanation}
        </Text>
      </Box>
    );
  })}
</VStack>
        </Box>
      )}
    </Box>
  );
};

export default SendTestResults;
